import { Button, Center, Text, TextInput, Group, Switch } from "@mantine/core";
import { useEffect, useState, useMemo } from "react";
import { AddAddressForm, EditAddressForm, ViewUsersForm } from "./forms/modals/AddressForms";
import { ApplicationAddressSystemType } from "../../types/ApplicationAddressSystem";
import { IconSearch, IconPlus } from "@tabler/icons-react";
import { UserType } from "../../types/User";
import { SortableTable, ColumnDef } from "../SortableTable";
import { getAllUsers } from "../../services/UserService";
import styles from './AddressTable.module.css';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface AddressTableProps {
    addressSystemData: ApplicationAddressSystemType[];
    refreshAddressSystems: () => void;
    refreshUsers: () => void;
}

export default function AddressTable({ addressSystemData, refreshAddressSystems, refreshUsers }: AddressTableProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({ pageId: 343, addressSystemId: 0, autoLoad: true });
    const [addressSearch, setAddressSearch] = useState<string>('');
    const [selectedID, setSelectedID] = useState<number>(-1);
    const [allUsers, setAllUsers] = useState<UserType[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<ApplicationAddressSystemType>({
        id: 0,
        name: '',
        status: 0,
        users: []
    });
    const [statusFilter, setStatusFilter] = useState<0 | 1>(0); 
    const pageSize = 100;
    const [addAddressOpened, setAddAddressOpened] = useState(false);
    const [editAddressOpened, setEditAddressOpened] = useState(false);
    const [viewUsersOpened, setViewUsersOpened] = useState(false);
    const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);

    const handleAddressSuccess = () => {
        refreshAddressSystems();
        refreshUsers();
    };

    useEffect(() => {
        async function fetchAllUsers() {
            const users = await getAllUsers();
            setAllUsers(users);
        }
        fetchAllUsers();
    }, []);

    useEffect(() => {
        const addressSystem = addressSystemData.find((item) => item.id === selectedID);
        if (addressSystem) {
            setSelectedAddress({ id: addressSystem.id, name: addressSystem.name, status: addressSystem.status, users: addressSystem.users });
        }
    }, [addressSearch, selectedID, addressSystemData, statusFilter]);

    // useEffect(() => {
    //     function updatePageSize() {
    //         const availableHeight = window.innerHeight - 300;
    //         const rows = Math.max(3, Math.floor(availableHeight / 48));
    //         setPageSize(rows);
    //     }
    //     updatePageSize();
    //     window.addEventListener('resize', updatePageSize);
    //     return () => window.removeEventListener('resize', updatePageSize);
    // }, []);

    const filteredAddress = useMemo(() => 
        addressSystemData
            .filter((item) => item.status === statusFilter)
            .filter((item) =>
                item.name.toLowerCase().includes(addressSearch.toLowerCase())
            ),
        [addressSystemData, statusFilter, addressSearch]
    );

    const columns: ColumnDef<ApplicationAddressSystemType>[] = [
        { header: getTranslation("LABEL_ADDRESS_TABLE_ID") || "Id", accessor: 'id', sortable: true, width: 200 },
        { header: getTranslation("LABEL_ADDRESS_TABLE_ADDRESS_SYSTEM") || "Address System", accessor: 'name', sortable: true, width: 800 },
    ];

    return (
        <div className={styles.container}>
            <>
                <AddAddressForm
                    opened={addAddressOpened}
                    open={() => setAddAddressOpened(true)}
                    close={() => setAddAddressOpened(false)}
                    onSuccess={handleAddressSuccess}
                    allAddressSystems={addressSystemData} 
                    allUsers={allUsers}/>

                <EditAddressForm
                    opened={editAddressOpened}
                    open={() => setEditAddressOpened(true)}
                    close={() => { setEditAddressOpened(false); setHighlightedRowId(null); }}
                    selectedAddress={selectedAddress}
                    onSuccess={handleAddressSuccess}
                    allAddressSystems={addressSystemData} 
                    allUsers={allUsers}/>

                <ViewUsersForm
                    opened={viewUsersOpened}
                    open={() => setViewUsersOpened(true)}
                    close={() => { setViewUsersOpened(false); setHighlightedRowId(null); }}
                    addressSystemId={selectedID}
                />
            </>

            <>
                <Group mb="md" justify="space-between" align="center" className={styles.headerGroup}>
                    <Switch
                        checked={statusFilter === 1}
                        onChange={(event) => setStatusFilter(event.currentTarget.checked ? 1 : 0)}
                        styles={(theme) => ({
                            track: {
                                backgroundColor: statusFilter === 0 ? 'gray' : undefined,
                                borderColor: statusFilter === 0 ? 'gray' : undefined,
                            }
                        })}
                    />
                    <Text fw={500} fz="sm">{getTranslation("LABEL_ADDRESS_TABLE_ARCHIVED") || "Archived"}</Text>
                    <TextInput
                        value={addressSearch}
                        onChange={(event) => { setAddressSearch(event.currentTarget.value) }}
                        placeholder={getTranslation("PLACEHOLDER_SEARCH_ADDRESS_SYSTEMS") || "Search address systems..."}
                        leftSection={<IconSearch size={18} color="#009DF0" />}
                        className={styles.searchInput}
                    />
                    <Button onClick={() => setAddAddressOpened(true)} rightSection={<IconPlus size={16} />}>
                        {getTranslation("LABEL_ADDRESS_TABLE_ADD_ADDRESS_SYSTEM") || "Add Address System"}
                    </Button>
                </Group>

                <SortableTable
                    data={filteredAddress}
                    columns={columns}
                    pageSize={pageSize}
                    rowKey={(row) => row.id}
                    rowStyle={(row) => 
                        highlightedRowId === row.id 
                            ? { backgroundColor: '#e3f0ff' }
                            : {}
                    }
                    actionColumns={[
                        {
                            label: getTranslation("LABEL_ADDRESS_TABLE_USERS") || "Users",
                            width: 100,
                            render: (row) => (
                                <Button onClick={() => { 
                                    setSelectedID(row.id); 
                                    setHighlightedRowId(row.id);
                                    setViewUsersOpened(true); 
                                }} 
                                w={60} 
                                disabled={row.users.length === 0}>
                                    {row.users.length}
                                </Button>
                            )
                        },
                        {
                            label: getTranslation("LABEL_ADDRESS_TABLE_EDIT") || "Edit",
                            width: 100,
                            render: (row) => (
                                <Button onClick={() => { 
                                    setSelectedID(row.id); 
                                    setHighlightedRowId(row.id);
                                    setEditAddressOpened(true); 
                                }}>
                                    {getTranslation("LABEL_ADDRESS_TABLE_EDIT") || "Edit"}
                                </Button>
                            )
                        }
                    ]}
                />
                {filteredAddress.length === 0 && (
                    <Center mt="xl">
                        <Text>{getTranslation("LABEL_ADDRESS_TABLE_NO_RECORDS") || "No records."}</Text>
                    </Center>
                )}
            </>
        </div>
    )
}